/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Check, X, Upload } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { notificationServices } from '@/Services';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import processFlowServices from '@/Services/Analytics Services/process-flow.services';
import { modalActions } from '@/Actions';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { getHigherUnitName, convertWeightUnit, imageToBase64, useProductData } from '@/Helpers';
import { AlertModal } from '@/Components/Modals';

const columns = [
    {
        title: 'S.No',
        key: 'sno',
        width: 60,
        customCol: true,
    },
    {
        title: 'Product Name',
        key: 'productName',
        width: 250,
        customCol: true,
    },
    {
        title: 'SKU / Variant ID',
        key: 'skuCode',
        width: 150,
        customCol: true,
    },
    {
        title: 'Qty',
        key: 'qty',
        width: 100,
        customCol: true,
    },
    {
        title: 'Unit',
        key: 'unit',
        width: 80,
        customCol: true,
    },
];

function PRApprovalModalContent({ rowData, refetch }) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [reason, setReason] = useState('');
    const [uploadedDoc, setUploadedDoc] = useState(null);
    const [uploadedDocUrl, setUploadedDocUrl] = useState('');

    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);
    const productList = useSelector((state) => state.form?.ALL_PRODUCTS?.data?.LIST || []);

    useEffect(() => {
        if (productList?.length === 0) {
            useProductData(dispatch);
        }
    }, []);

    const { data: detailResponse, isLoading: isLoadingDetail } = useQuery({
        queryKey: ['purchaseRequestDetail', rowData?.id],
        queryFn: () => purchaseRequestServices.fetchPurchaseRequestDetail({ data: { prId: rowData?.id } }),
        enabled: !!rowData?.id,
    });

    const prDetail = detailResponse?.data || rowData;

    const { data: processNodes } = useQuery({
        queryKey: ['processNodes', prDetail?.storeId],
        queryFn: () => processFlowServices.fetchNodes({ params: { storeId: prDetail?.storeId } }),
        enabled: !!prDetail?.storeId,
    });

    const storeName = useMemo(() => {
        const store = storeList.find(s => s.id === prDetail?.storeId);
        return store?.name || '-';
    }, [storeList, prDetail?.storeId]);

    const processName = useMemo(() => {
        const nodes = processNodes?.data?.nodes || [];
        const node = nodes.find(n => n.id?.toString() === prDetail?.processId?.toString());
        return node ? `${node.data?.heading} - ${node.data?.sectionName}` : '-';
    }, [processNodes, prDetail?.processId]);

    // Build a variant lookup map from productList
    const variantMap = useMemo(() => {
        const map = {};
        productList?.forEach((product) => {
            product?.variants?.forEach((variant) => {
                map[variant?.id] = {
                    productName: product?.productName,
                    displayName: variant?.displayName,
                    skuCode: variant?.clientVariantId || product?.skuCode,
                    unit: product?.unit,
                    skuType: product?.skuType,
                };
            });
        });
        return map;
    }, [productList]);

    const items = useMemo(() => {
        try {
            const itemsArray = prDetail?.items || [];
            let rawItems = itemsArray;

            if (rawItems.length === 0) {
                const data = prDetail?.jsonData;
                if (typeof data === 'string') {
                    rawItems = JSON.parse(data) || [];
                } else {
                    rawItems = data || [];
                }
            }

            return rawItems.map((item) => {
                const variantInfo = variantMap[item.entityId] || {};
                const baseUnit = variantInfo.unit || item.unit || '';
                const higherUnit = getHigherUnitName(baseUnit);
                return {
                    ...item,
                    productName: item.productName || `${variantInfo.productName || ''}${variantInfo.displayName ? ` (${variantInfo.displayName})` : ''}`,
                    skuCode: item.skuCode || variantInfo.skuCode || '-',
                    unit: higherUnit || baseUnit,
                    rawUnit: baseUnit,
                };
            });
        } catch (e) {
            return [];
        }
    }, [prDetail?.jsonData, prDetail?.items, variantMap]);

    const filteredItems = useMemo(() => {
        return items.filter(item =>
            item.productName?.toLowerCase().includes(search.toLowerCase()) ||
            item.skuCode?.toLowerCase().includes(search.toLowerCase())
        );
    }, [items, search]);

    const { mutate: updateStatus, isLoading: isUpdating } = useMutation({
        mutationFn: purchaseRequestServices.updatePurchaseRequestStatus,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Purchase Request updated successfully',
                });
                queryClient.invalidateQueries({ queryKey: ['purchaseRequests'] });
                if (refetch) refetch();
                dispatch(modalActions.toggleCustomModal(false));
            } else {
                notificationServices.generateNotification({
                    type: 'error',
                    message: res.message || 'Error updating status',
                });
            }
        },
        onError: (err) => {
            notificationServices.generateNotification({
                type: 'error',
                message: err.message || 'Operation failed',
            });
        }
    });

    const handleAction = (action) => {
        updateStatus({
            data: {
                action: action,
                entityType: 'PurchaseRequest',
                entityId: rowData.id,
                extra: {
                    uploadedDoc: uploadedDocUrl || '',
                    reason: reason,
                },
            }
        });
    };

    const showConfirmation = (action) => {
        if (!uploadedDocUrl) {
            notificationServices.generateNotification({
                type: 'error',
                message: 'Please upload a document before proceeding',
            });
            return;
        }

        const isApprove = action === 'APPROVE';
        dispatch(modalActions.toggleAlert(true, {
            heading: isApprove ? 'Approve Purchase Request' : 'Reject Purchase Request',
            message: isApprove
                ? `Are you sure you want to approve PR #${rowData.id}?`
                : `Are you sure you want to reject PR #${rowData.id}?`,
            confirmText: isApprove ? 'Yes, Approve' : 'Yes, Reject',
            cancelText: 'Cancel',
            colorReverse: isApprove,
            handleConfirm: () => {
                dispatch(modalActions.toggleAlert(false));
                handleAction(action);
            },
        }));
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadedDoc(file);
            const base64Url = await imageToBase64(file);
            setUploadedDocUrl(base64Url);
        }
    };

    const handleRemoveDoc = () => {
        setUploadedDoc(null);
        setUploadedDocUrl('');
    };

    const customRowData = {
        sno: (row, index) => index + 1,
        productName: (row) => <span className='text-gray-700 font-medium ml-3'>{row.productName}</span>,
        skuCode: (row) => <span className='bg-gray-100 px-2 py-0.5 rounded text-[10px] font-mono ml-3'>{row.skuCode}</span>,
        qty: (row) => {
            const converted = convertWeightUnit(row.qty, row.rawUnit, 'IND', true);
            return <span className='font-bold text-gray-800 ml-3'>{converted}</span>;
        },
        unit: (row) => <span className='text-gray-600 ml-3'>{row.unit || '-'}</span>,
    };

    return (
        <div className='flex flex-col h-full bg-white relative'>
            <AlertModal />
            <div className='p-2 border-b border-gray-100'>
                <div className='flex items-center justify-between mb-2'>
                    <h2 className='text-lg font-bold text-center w-full'>View Purchase Request Details</h2>
                </div>
            </div>

            {/* Info Grid */}
            <div className='px-4 py-3 bg-gray-50/50 border-b border-gray-100'>
                <div className='grid grid-cols-4 gap-4'>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>PR ID / Name</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.description || `PR #${prDetail?.id}`}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Store</p>
                        <p className='text-[13px] text-gray-700 font-medium truncate' title={storeName}>{storeName}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Process</p>
                        <p className='text-[13px] text-gray-700 font-medium truncate' title={processName}>{processName}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>PR Date</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.createdAt ? new Date(prDetail?.createdAt).toISOString().split('T')[0] : '-'}</p>
                    </div>
                </div>
            </div>

            {/* Table Search + Upload */}
            <div className='px-6 pt-4'>
                <div className='flex items-center gap-3'>
                    <div className='relative flex-1'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' size={18} />
                        <Input
                            placeholder='Filter items by name or SKU...'
                            className='pl-10 h-10 border-gray-200 focus:bg-white transition-all'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='flex items-center gap-2'>
                        <input
                            type='file'
                            id='doc-upload'
                            className='hidden'
                            onChange={handleFileUpload}
                            accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg'
                        />
                        <label
                            htmlFor='doc-upload'
                            className='flex items-center gap-2 px-3 h-10 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm text-gray-600'
                        >
                            <Upload size={16} />
                            <span>{uploadedDoc ? uploadedDoc.name : 'Upload Doc*'}</span>
                        </label>
                        {uploadedDoc && (
                            <button
                                onClick={handleRemoveDoc}
                                className='text-gray-400 hover:text-red-500 transition-colors'
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className='h-[calc(100vh-500px)] overflow-auto mt-2'>
                <VariantTable
                    columns={columns}
                    data={filteredItems}
                    customRowData={customRowData}
                    isLoading={isLoadingDetail}
                    customCol={(col) => <span className='text-[12px] font-bold text-gray-500 uppercase ml-3'>{col.title}</span>}
                />
            </div>

            {/* Footer Actions */}
            <div className='p-4 border-t border-gray-100'>
                <div className='flex items-end gap-4'>
                    <div className='flex-1 space-y-1'>
                        <label className='text-xs font-semibold text-gray-500'>Approval / Rejection Reason:</label>
                        <textarea
                            className='w-full h-16 p-2.5 rounded-lg border border-gray-200 focus:ring-1 focus:ring-blue-500 outline-none transition-all resize-none text-sm'
                            placeholder='Enter your reason here...'
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    <div className='flex gap-2 pb-0.5'>
                        <Button
                            variant='outline'
                            className='bg-red-500 hover:bg-red-600 text-white font-bold h-10 px-6 transition-colors border-none text-sm'
                            onClick={() => showConfirmation('REJECT')}
                            disabled={isUpdating}
                        >
                            Reject
                        </Button>
                        <Button
                            className='bg-green-800 hover:bg-green-900 text-white font-bold h-10 px-6 text-sm'
                            onClick={() => showConfirmation('APPROVE')}
                            disabled={isUpdating}
                        >
                            Approve
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PRApprovalModalContent;
